import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, Typography, Box, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Flex } from 'reflexbox';
import PageHeader from './gamePlayComponents/PageHeader';
import { useDispatch, useSelector } from 'react-redux';
import { getMemberIdsForGivenTeamId, writeCurrentTeamIdData, writeCourseSelection, writeUserIntent } from '../helpers/dbFunctions';
import { createNewCurrentTeamId } from '../redux/teamSlice';
import { updateUserIntent } from '../redux/userSlice';
import * as routes from "./helpers/routes";
import { useNavigate } from 'react-router';
const TENTATIVE_SELECTED_COURSE_KEY = 'tentativeSelectedCourse';

const generateRandomCode = () => {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return code;
};

const JoinOrHost = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const inputRef = useRef(null);

  const handleJoinClick = () => {
    setJoinDialogOpen(true);
  };

  // Focus the hidden input when dialog opens
  useEffect(() => {
    if (joinDialogOpen && inputRef.current) {
      // Use requestAnimationFrame for better focus timing
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }, 150);
      });
    }
  }, [joinDialogOpen]);

  const handleJoinCodeChange = (event) => {
    const value = event.target.value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    // Limit to 6 characters
    if (value.length <= 6) {
      setJoinCode(value);
      setJoinError('');
    }
  };

  const handleBoxClick = (event) => {
    event.preventDefault();
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Backspace' && joinCode.length === 0) {
      event.preventDefault();
    }
  };

  const uid = useSelector((state) => state.user?.uid);

  const handleJoinSubmit = async () => {
    setJoinError('');
    const code = (joinCode || '').trim();
    if (code.length !== 6) {
      setJoinError(`Codes are 6 characters. Yours is ${code.length}`);
      return false;
    }
    const newMemberIdsArray = await getMemberIdsForGivenTeamId(code);
    //
    // TODO: Also need to check the game state to make sure its in the lobby state
    //
    if (newMemberIdsArray) {
      // add my userID to the array of existing users already on the team
      if (!newMemberIdsArray.includes(uid)) newMemberIdsArray.push(uid);
      // update the back-end in both places (team association under uid & member array under teamId)
      await writeCurrentTeamIdData(uid, code, newMemberIdsArray);
      // Clear user intent when joining a team (no longer hosting or solo)
      writeUserIntent(uid, null);
      dispatch(updateUserIntent(null));
      // close dialog on success
      setJoinDialogOpen(false);
      setJoinCode('');
      setJoinError('');
      navigate(routes.LOBBY)
      return true;
    } else {
      setJoinError('There is no one hosting a team with that code');
      return false;
    }
  };

  const handleHostClick = async () => {
    const pendingCourse = sessionStorage.getItem(TENTATIVE_SELECTED_COURSE_KEY);
    const code = generateRandomCode();
    await writeCurrentTeamIdData(uid, code, undefined, uid);
    dispatch(createNewCurrentTeamId({ currentTeamId: code }));
    writeUserIntent(uid, 'HOST');
    dispatch(updateUserIntent('HOST'));
    if (pendingCourse) {
      sessionStorage.removeItem(TENTATIVE_SELECTED_COURSE_KEY);
      writeCourseSelection(code, pendingCourse);
      navigate(routes.LOBBY);
    } else {
      navigate(routes.COURSESELECTOR_HOST);
    }
  };

  const handlePlaySoloClick = async () => {
    // Same flow as Host: create team, then course selection → lobby. Lobby hides team/code for solo.
    const pendingCourse = sessionStorage.getItem(TENTATIVE_SELECTED_COURSE_KEY);
    const code = generateRandomCode();
    await writeCurrentTeamIdData(uid, code, undefined, uid);
    dispatch(createNewCurrentTeamId({ currentTeamId: code }));
    writeUserIntent(uid, 'SOLO');
    dispatch(updateUserIntent('SOLO'));
    if (pendingCourse) {
      sessionStorage.removeItem(TENTATIVE_SELECTED_COURSE_KEY);
      writeCourseSelection(code, pendingCourse);
      navigate(routes.LOBBY);
    } else {
      navigate(routes.COURSESELECTOR_HOST);
    }
  };

  const cardStyle = {
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    borderRadius: '16px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 4,
    }
  };

  return (
  <>
      <PageHeader goHome title="Start a game" />
      
      <Flex style={{ width: '100%', gap: '16px', flexDirection: 'column', marginTop: '16px' }}>
        {/* Join Card */}
        <Card 
          sx={cardStyle}
          onClick={handleJoinClick}
        >
          <CardContent sx={{ padding: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Join a Team
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use a code from your game host
              </Typography>
            </Box>
            <ArrowForward sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
          </CardContent>
        </Card>

        {/* Host Card */}
        <Card 
          sx={cardStyle}
          onClick={handleHostClick}
        >
          <CardContent sx={{ padding: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Host a Team
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a course and share your join code
              </Typography>
            </Box>
            <ArrowForward sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
          </CardContent>
        </Card>

        {/* Play Solo Card */}
        <Card 
          sx={cardStyle}
          onClick={handlePlaySoloClick}
        >
          <CardContent sx={{ padding: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Play Solo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a course and get started
              </Typography>
            </Box>
            <ArrowForward sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
          </CardContent>
        </Card>
      </Flex>

      {/* Join Team Dialog (MUI) */}
      <Dialog
        open={joinDialogOpen}
        onClose={() => {
          setJoinDialogOpen(false);
          setJoinCode('');
          setJoinError('');
        }}
        aria-labelledby="join-team-dialog-title"
      >
        <DialogTitle id="join-team-dialog-title">Join a Team</DialogTitle>
        <DialogContent>
        <Typography sx={{mb: 3}}>Enter the code provided by a host</Typography>
          <Flex style={{ flexDirection: 'column', alignItems: 'center' }}>
            {/* Hidden input for actual functionality */}
            <TextField
              inputRef={inputRef}
              value={joinCode}
              onChange={handleJoinCodeChange}
              onKeyDown={handleKeyDown}
              autoFocus
              inputProps={{ 
                maxLength: 6, 
                style: { 
                  textTransform: 'uppercase',
                  position: 'absolute',
                  opacity: 0,
                  pointerEvents: 'none',
                  width: '1px',
                  height: '1px'
                } 
              }}
              sx={{ position: 'absolute', left: '-9999px' }}
            />

            {/* Visual 6-box display */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: '4px', sm: '8px' },
                //marginBottom: '16px',
                cursor: 'text',
                width: '100%',
                maxWidth: '300px',
                justifyContent: 'space-between',
              }}
              onClick={handleBoxClick}
            >
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Box
                  key={index}
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{
                    flex: '1 1 0',
                    minWidth: '30px',
                    maxWidth: '48px',
                    width: '100%',
                    height: { xs: '45px', sm: '50px' },
                    border: '2px solid',
                    borderColor: joinCode.length === index ? 'primary.main' : 'grey.300',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '18px', sm: '20px' },
                    fontWeight: 'bold',
                    backgroundColor: joinCode.length === index ? 'action.selected' : 'background.paper',
                    transition: 'all 0.2s ease-in-out',
                    outline: 'none',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                    '&:focus': {
                      outline: 'none',
                      borderColor: joinCode.length === index ? 'primary.main' : 'grey.300',
                    }
                  }}
                >
                  {joinCode[index] || ''}
                </Box>
              ))}
            </Box>

            <Typography variant="caption" style={{ minWidth: '2px', minHeight: '25px' }} color="secondary">
              {joinError || ' '}
            </Typography>
          </Flex>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setJoinDialogOpen(false);
            setJoinCode('');
            setJoinError('');
          }}>Cancel</Button>
          <Button onClick={async () => { await handleJoinSubmit(); }} >
            Join
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JoinOrHost;
